import { useEffect } from 'react';

interface PageMeta {
  title: string;
  description: string;
  canonicalUrl: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  /** Inject one or more Schema.org JSON-LD blocks for this page (e.g. Service,
   *  BreadcrumbList, FAQPage). The hook tags them with `data-page-jsonld` so it
   *  can clean them up on unmount and never collide with the static blocks in
   *  index.html. */
  jsonLd?: object[];
  /** Keep this page out of search engines (e.g. the logged-in talent portal). */
  noindex?: boolean;
}

function setMetaTag(property: string, content: string) {
  // Try property attribute first (og: tags), then name attribute
  let el = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null;
  if (!el) el = document.querySelector(`meta[name="${property}"]`) as HTMLMetaElement | null;
  if (el) {
    el.setAttribute('content', content);
  }
}

function setCanonical(url: string) {
  let el = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (el) el.href = url;
}

// Store original values on first call
let originals: Record<string, string> | null = null;
function captureOriginals() {
  if (originals) return;
  originals = {
    title: document.title,
    description: document.querySelector('meta[name="description"]')?.getAttribute('content') || '',
    'og:title': document.querySelector('meta[property="og:title"]')?.getAttribute('content') || '',
    'og:description': document.querySelector('meta[property="og:description"]')?.getAttribute('content') || '',
    'og:url': document.querySelector('meta[property="og:url"]')?.getAttribute('content') || '',
    'og:image': document.querySelector('meta[property="og:image"]')?.getAttribute('content') || '',
    'twitter:title': document.querySelector('meta[name="twitter:title"]')?.getAttribute('content') || '',
    'twitter:description': document.querySelector('meta[name="twitter:description"]')?.getAttribute('content') || '',
    'twitter:image': document.querySelector('meta[name="twitter:image"]')?.getAttribute('content') || '',
    canonical: (document.querySelector('link[rel="canonical"]') as HTMLLinkElement)?.href || '',
  };
}

function injectJsonLd(blocks: object[]): HTMLScriptElement[] {
  const inserted: HTMLScriptElement[] = [];
  blocks.forEach((block) => {
    const el = document.createElement('script');
    el.type = 'application/ld+json';
    el.setAttribute('data-page-jsonld', 'true');
    el.textContent = JSON.stringify(block);
    document.head.appendChild(el);
    inserted.push(el);
  });
  return inserted;
}

export function usePageMeta(meta: PageMeta) {
  useEffect(() => {
    captureOriginals();

    document.title = meta.title;
    setMetaTag('description', meta.description);
    setMetaTag('og:title', meta.ogTitle || meta.title);
    setMetaTag('og:description', meta.ogDescription || meta.description);
    setMetaTag('og:url', meta.canonicalUrl);
    setMetaTag('twitter:title', meta.ogTitle || meta.title);
    setMetaTag('twitter:description', meta.ogDescription || meta.description);
    if (meta.ogImage) {
      setMetaTag('og:image', meta.ogImage);
      setMetaTag('twitter:image', meta.ogImage);
    }
    setCanonical(meta.canonicalUrl);

    const insertedJsonLd = meta.jsonLd?.length ? injectJsonLd(meta.jsonLd) : [];

    let robotsEl: HTMLMetaElement | null = null;
    if (meta.noindex) {
      robotsEl = document.createElement('meta');
      robotsEl.name = 'robots';
      robotsEl.content = 'noindex, nofollow';
      robotsEl.setAttribute('data-page-robots', 'true');
      document.head.appendChild(robotsEl);
    }

    return () => {
      robotsEl?.remove();
      if (originals) {
        document.title = originals.title;
        setMetaTag('description', originals.description);
        setMetaTag('og:title', originals['og:title']);
        setMetaTag('og:description', originals['og:description']);
        setMetaTag('og:url', originals['og:url']);
        setMetaTag('og:image', originals['og:image']);
        setMetaTag('twitter:title', originals['twitter:title']);
        setMetaTag('twitter:description', originals['twitter:description']);
        setMetaTag('twitter:image', originals['twitter:image']);
        setCanonical(originals.canonical);
      }
      // Clean up any injected JSON-LD blocks on unmount.
      insertedJsonLd.forEach((el) => el.remove());
    };
  }, [meta.title, meta.description, meta.canonicalUrl, meta.ogTitle, meta.ogDescription, meta.ogImage, meta.jsonLd, meta.noindex]);
}
